
import { useState, useRef } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { ArrowLeft, User, Mail, Calendar, TrendingUp, Edit2, Check, X, Shield, AlertTriangle, Camera, Trash2 } from "lucide-react"
import { useAuth } from "@/lib/auth/auth-context"
import { useTransactions } from "@/lib/context/transaction-context"
import { toast } from "sonner"
import { format } from "date-fns"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"

interface ProfileProps {
  onBack: () => void
}

export function Profile({ onBack }: ProfileProps) {
  const { user, updateProfile, updatePassword, deleteAccount, uploadAvatar, deleteAvatar } = useAuth()
  const { transactions } = useTransactions()
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [firstName, setFirstName] = useState(user?.user_metadata?.first_name || '')
  const [lastName, setLastName] = useState(user?.user_metadata?.last_name || '')
  const [isDeleting, setIsDeleting] = useState(false)
  const [showPasswordDialog, setShowPasswordDialog] = useState(false)
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const getUserInitials = () => {
    if (!user) return "U"
    const firstName = user.user_metadata?.first_name
    const lastName = user.user_metadata?.last_name
    if (!firstName && !lastName) return "U"
    if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase()
    return (firstName || lastName || 'U')[0].toUpperCase()
  }

  const getMemberSince = () => {
    if (!user?.created_at) return "Unknown"
    return format(new Date(user.created_at), "MMMM yyyy")
  }

  const handleSaveProfile = async () => {
    if (!firstName.trim()) {
      toast.error("First name cannot be empty")
      return
    }
    if (!lastName.trim()) {
      toast.error("Last name cannot be empty")
      return
    }

    setIsSaving(true)
    const { error } = await updateProfile(firstName.trim(), lastName.trim())
    setIsSaving(false)

    if (error) {
      toast.error(`Failed to update profile: ${error.message}`)
    } else {
      toast.success("Profile updated successfully")
      setIsEditing(false)
    }
  }

  const handleCancelEdit = () => {
    setFirstName(user?.user_metadata?.first_name || '')
    setLastName(user?.user_metadata?.last_name || '')
    setIsEditing(false)
  }

  const handleChangePassword = () => {
    setShowPasswordDialog(true)
  }

  const handlePasswordSubmit = async () => {
    // Strong password validation
    const { validatePassword } = await import("@/lib/utils/password-validation")
    const validation = validatePassword(newPassword)

    if (!validation.isValid) {
      toast.error(validation.errors[0]) // Show first error
      return
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match")
      return
    }

    setIsChangingPassword(true)
    const { error } = await updatePassword(newPassword)
    setIsChangingPassword(false)

    if (error) {
      toast.error(`Failed to update password: ${error.message}`)
    } else {
      toast.success("Password updated successfully")
      setShowPasswordDialog(false)
      setNewPassword("")
      setConfirmPassword("")
    }
  }

  const handlePasswordDialogClose = (open: boolean) => {
    setShowPasswordDialog(open)
    if (!open) {
      setNewPassword("")
      setConfirmPassword("")
    }
  }

  const handlePasswordKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isChangingPassword) {
      e.preventDefault()
      handlePasswordSubmit()
    }
  }

  const handleDeleteAccount = async () => {
    setIsDeleting(true)
    const { error } = await deleteAccount()
    setIsDeleting(false)

    if (error) {
      toast.error(`Failed to delete account: ${error.message}`)
    } else {
      toast.success("Account deleted successfully")
    }
  }

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setIsUploadingAvatar(true)
    const { url, error } = await uploadAvatar(file)
    setIsUploadingAvatar(false)

    if (error) {
      toast.error(`Failed to upload avatar: ${error.message}`)
    } else if (url) {
      toast.success("Avatar updated successfully")
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleDeleteAvatar = async () => {
    setIsUploadingAvatar(true)
    const { error } = await deleteAvatar()
    setIsUploadingAvatar(false)

    if (error) {
      toast.error(`Failed to remove avatar: ${error.message}`)
    } else {
      toast.success("Avatar removed successfully")
    }
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="mx-auto max-w-md space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-6 w-6" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">Profile</h1>
            <p className="text-sm text-muted-foreground">Manage your account</p>
          </div>
        </div>

        {/* Profile Card */}
        <Card className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="relative group">
                <Avatar className="h-24 w-24 border-4 border-white/20">
                  <AvatarImage src={user?.user_metadata?.avatar_url} alt="Profile" />
                  <AvatarFallback className="bg-white/20 text-3xl font-bold text-primary-foreground">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                  onChange={handleAvatarChange}
                  className="hidden"
                />
                <Button
                  size="icon"
                  variant="secondary"
                  className="absolute bottom-0 right-0 h-8 w-8 rounded-full shadow-lg"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingAvatar}
                >
                  <Camera className="h-4 w-4" />
                </Button>
              </div>
              <div>
                <h2 className="text-3xl font-bold">
                  {`${user?.user_metadata?.first_name || ''} ${user?.user_metadata?.last_name || ''}`.trim() || 
                   user?.email?.split("@")[0] || "User"}
                </h2>
                <p className="text-sm opacity-90 mt-1">{user?.email}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Account Information
            </CardTitle>
            <CardDescription>Your personal details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first-name">First Name</Label>
                <Input
                  id="first-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={!isEditing}
                  placeholder="First name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="last-name">Last Name</Label>
                <Input
                  id="last-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={!isEditing}
                  placeholder="Last name"
                />
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              {!isEditing ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit Name
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSaveProfile}
                    disabled={isSaving}
                    className="text-green-600 hover:text-green-700"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    className="text-destructive hover:text-destructive"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </>
              )}
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <div className="flex gap-2">
                <Input value={user?.email || ""} disabled />
                <Button variant="outline" size="icon" disabled>
                  <Mail className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Email cannot be changed</p>
            </div>
          </CardContent>
        </Card>

        {/* Statistics */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Statistics
            </CardTitle>
            <CardDescription>Your account activity</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Member Since</p>
                  <p className="font-semibold">{getMemberSince()}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Transactions</p>
                  <p className="font-semibold">{transactions.length}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Security
            </CardTitle>
            <CardDescription>Manage your account security</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {user?.user_metadata?.avatar_url && (
              <Button
                variant="outline"
                className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                onClick={handleDeleteAvatar}
                disabled={isUploadingAvatar}
              >
                <Trash2 className="h-4 w-4" />
                Remove Profile Photo
              </Button>
            )}
            <Button
              variant="outline"
              className="w-full justify-start gap-3"
              onClick={handleChangePassword}
            >
              <Shield className="h-4 w-4" />
              Change Password
            </Button>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 text-destructive hover:bg-destructive/10"
                >
                  <AlertTriangle className="h-4 w-4" />
                  Delete Account
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete your account
                    and remove all your data from our servers.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteAccount}
                    disabled={isDeleting}
                    className="bg-destructive hover:bg-destructive/90"
                  >
                    {isDeleting ? "Deleting..." : "Delete Account"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>

        {/* Password Change Dialog */}
        <AlertDialog open={showPasswordDialog} onOpenChange={handlePasswordDialogClose}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Change Password</AlertDialogTitle>
              <AlertDialogDescription>
                Enter your new password below. It must be at least 12 characters with uppercase, lowercase, number, and special character.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  onKeyDown={handlePasswordKeyDown}
                  placeholder="Enter new password"
                  disabled={isChangingPassword}
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyDown={handlePasswordKeyDown}
                  placeholder="Confirm new password"
                  disabled={isChangingPassword}
                />
              </div>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isChangingPassword}>Cancel</AlertDialogCancel>
              <Button
                onClick={handlePasswordSubmit}
                disabled={isChangingPassword}
              >
                {isChangingPassword ? "Updating..." : "Update Password"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  )
}
